import React, { useState, useEffect } from 'react';
import { apiService } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Link } from 'react-router-dom';

// Store Settings Tab Component
const StoreSettingsTab = ({ shops }) => {
  const { isDarkMode } = useTheme();
  const [storeSettings, setStoreSettings] = useState({
    defaultShopId: '',
    storeName: 'Tafsir Art Store',
    storeEnabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStoreSettings();
  }, []);

  // Debug: Log whenever storeSettings changes
  useEffect(() => {
    console.log('📊 Store settings state updated:', storeSettings);
  }, [storeSettings]);

  const fetchStoreSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/settings/store');
      console.log('📥 Fetched store settings response:', response);
      
      // Backend returns: { _id, type, data: {...}, updatedAt }
      // We need the 'data' field which contains the actual settings
      if (response && response.data) {
        // Access the nested 'data' field from the Settings object
        const settingsData = response.data.data || response.data;
        console.log('✅ Store settings data:', settingsData);
        console.log('🔍 Data type:', Array.isArray(settingsData) ? 'ARRAY' : typeof settingsData);
        
        // Check if data is a clean object (not an array)
        if (!Array.isArray(settingsData) && typeof settingsData === 'object') {
          // Extract only the actual settings fields we need
          const cleanSettings = {
            defaultShopId: settingsData.defaultShopId || '',
            storeName: settingsData.storeName || 'Tafsir Art Store',
            storeEnabled: settingsData.storeEnabled !== undefined ? settingsData.storeEnabled : true
          };
          console.log('🧹 Clean settings:', cleanSettings);
          setStoreSettings(cleanSettings);
        } else {
          console.warn('⚠️ Data is corrupted (array or wrong type), resetting to defaults');
          setStoreSettings({
            defaultShopId: '',
            storeName: 'Tafsir Art Store',
            storeEnabled: true
          });
        }
      } else if (response) {
        // Fallback if response structure is different
        console.warn('⚠️ Unexpected response structure:', response);
        setStoreSettings({
          defaultShopId: '',
          storeName: 'Tafsir Art Store',
          storeEnabled: true
        });
      }
    } catch (err) {
      console.log('⚠️ Store settings not configured yet:', err);
      // Initialize with default values if not found
      setStoreSettings({
        defaultShopId: '',
        storeName: 'Tafsir Art Store',
        storeEnabled: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Create a clean settings object with only the fields we need
      const cleanSettings = {
        defaultShopId: storeSettings.defaultShopId || '',
        storeName: storeSettings.storeName || 'Tafsir Art Store',
        storeEnabled: storeSettings.storeEnabled !== undefined ? storeSettings.storeEnabled : true
      };
      
      console.log('💾 Saving store settings (cleaned):', cleanSettings);
      console.log('🔍 Original state had keys:', Object.keys(storeSettings));
      
      // Send the CLEAN settings data to the backend
      const response = await apiService.put('/settings/store', cleanSettings);
      console.log('✅ Save response:', response);
      
      alert('✅ Store settings saved successfully!');
      
      // Refresh settings after save to confirm
      await fetchStoreSettings();
    } catch (err) {
      console.error('❌ Failed to save settings:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown error';
      alert('❌ Failed to save settings: ' + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={`text-center py-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading settings...</div>;
  }

  return (
    <div>
      <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-primary'}`}>Store Settings</h2>
      
      <div className="max-w-2xl space-y-6">
        <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-blue-900/30 border-blue-700 text-blue-200' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
          <p className="text-sm">
            <strong>Important:</strong> The default shop ID determines which Printify shop is used for the public store.
            Only published products from this shop will be visible to customers.
          </p>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Default Shop ID
          </label>
          <select
            value={storeSettings.defaultShopId || ''}
            onChange={(e) => {
              const newShopId = e.target.value;
              console.log('🔄 Shop ID changed to:', newShopId);
              setStoreSettings({ ...storeSettings, defaultShopId: newShopId });
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary ${
              isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="">Select a shop...</option>
            {shops.map(shop => (
              <option key={shop.id} value={shop.id}>
                {shop.title} ({shop.id})
              </option>
            ))}
          </select>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            This shop will be used for all public store product listings
            {storeSettings.defaultShopId && (
              <span className="font-semibold"> • Currently selected: {storeSettings.defaultShopId}</span>
            )}
          </p>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Store Name
          </label>
          <input
            type="text"
            value={storeSettings.storeName}
            onChange={(e) => setStoreSettings({ ...storeSettings, storeName: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary ${
              isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            placeholder="Tafsir Art Store"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="storeEnabled"
            checked={storeSettings.storeEnabled}
            onChange={(e) => setStoreSettings({ ...storeSettings, storeEnabled: e.target.checked })}
            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
          />
          <label htmlFor="storeEnabled" className={`ml-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Enable public store
          </label>
        </div>

        <button
          onClick={handleSave}
          disabled={saving || !storeSettings.defaultShopId}
          className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

const PrintifyAdmin = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchShops();
    }
  }, [user]);

  const fetchShops = async () => {
    try {
      const response = await apiService.get('/printify/shops');
      setShops(response);
      if (response.length > 0) {
        setSelectedShop(response[0].id);
      }
    } catch (err) {
      console.error('Error fetching shops:', err);
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`rounded-lg p-8 border ${isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
          <h3 className={`font-bold text-xl mb-2 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`}>Access Denied</h3>
          <p className={isDarkMode ? 'text-red-300' : 'text-red-600'}>You must be an admin to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div>
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
          <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading Printify data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-8 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container-custom">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-4xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-primary'}`}>
            Printify Admin Dashboard
          </h1>
          <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Manage your Printify products, orders, and settings
          </p>
        </div>

        {/* Shop Selector */}
        {shops.length > 0 && (
          <div className={`rounded-lg shadow-lg p-6 mb-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Selected Shop
            </label>
            <select
              value={selectedShop || ''}
              onChange={(e) => setSelectedShop(e.target.value)}
              className={`px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent w-full md:w-auto ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              {shops.map(shop => (
                <option key={shop.id} value={shop.id}>
                  {shop.title} ({shop.sales_channel})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className={`rounded-lg shadow-lg mb-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <nav className="flex -mb-px flex-wrap">
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'settings'
                    ? 'border-b-2 border-primary text-primary'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                ⚙️ Store Settings
              </button>
              <button
                onClick={() => setActiveTab('products')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'products'
                    ? 'border-b-2 border-primary text-primary'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                📦 Products
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'orders'
                    ? 'border-b-2 border-primary text-primary'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                🛒 Orders
              </button>
              <button
                onClick={() => setActiveTab('catalog')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'catalog'
                    ? 'border-b-2 border-primary text-primary'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                📚 Catalog
              </button>
              <button
                onClick={() => setActiveTab('webhooks')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'webhooks'
                    ? 'border-b-2 border-primary text-primary'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                🔗 Webhooks
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'settings' && (
              <StoreSettingsTab shops={shops} />
            )}
            {activeTab === 'products' && selectedShop && (
              <ProductsTab shopId={selectedShop} />
            )}
            {activeTab === 'orders' && selectedShop && (
              <OrdersTab shopId={selectedShop} />
            )}
            {activeTab === 'catalog' && (
              <CatalogTab />
            )}
            {activeTab === 'webhooks' && selectedShop && (
              <WebhooksTab shopId={selectedShop} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Products Tab Component
const ProductsTab = ({ shopId }) => {
  const { isDarkMode } = useTheme();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, [shopId]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/printify/shops/${shopId}/products`);
      setProducts(response.data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (productId) => {
    if (!window.confirm('Publish this product?')) return;
    
    try {
      await apiService.post(`/printify/shops/${shopId}/products/${productId}/publish`, {
        title: true,
        description: true,
        images: true,
        variants: true,
        tags: true
      });
      alert('Product published successfully!');
      fetchProducts();
    } catch (err) {
      alert('Failed to publish product: ' + err.message);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product? This action cannot be undone.')) return;
    
    try {
      await apiService.delete(`/printify/shops/${shopId}/products/${productId}`);
      alert('Product deleted successfully!');
      fetchProducts();
    } catch (err) {
      alert('Failed to delete product: ' + err.message);
    }
  };

  if (loading) {
    return <div className={`text-center py-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading products...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-primary'}`}>
          Products ({products.length})
        </h2>
        <Link
          to="/admin/printify/create-product"
          className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
        >
          + Create Product
        </Link>
      </div>

      {products.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No products found. Create your first product!
        </div>
      ) : (
        <div className="space-y-4">
          {products.map(product => (
            <div key={product.id} className={`border rounded-lg p-4 flex gap-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
              <img
                src={product.images?.[0]?.src || 'https://via.placeholder.com/100'}
                alt={product.title}
                className="w-24 h-24 object-cover rounded shadow-md"
              />
              <div className="flex-1">
                <h3 className={`font-bold text-lg mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{product.title}</h3>
                <p className={`text-sm line-clamp-2 mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{product.description}</p>
                <div className="flex gap-4 mt-2 flex-wrap">
                  <span className={`text-sm px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                    {product.variants?.length || 0} variants
                  </span>
                  <span className={`text-sm px-2 py-1 rounded font-medium ${product.visible ? 'bg-green-100 text-green-700' : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    {product.visible ? '✓ Visible' : '✕ Hidden'}
                  </span>
                  {product.is_locked && (
                    <span className="text-sm px-2 py-1 rounded bg-yellow-100 text-yellow-700 font-medium">🔒 Locked</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handlePublish(product.id)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors font-medium"
                >
                  Publish
                </button>
                <Link
                  to={`/admin/printify/edit-product/${product.id}`}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-center transition-colors font-medium"
                >
                  Edit
                </Link>
                <button
                  onClick={() => handleDelete(product.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Orders Tab Component
const OrdersTab = ({ shopId }) => {
  const { isDarkMode } = useTheme();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, [shopId]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/printify/shops/${shopId}/orders`);
      setOrders(response.data || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className={`text-center py-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading orders...</div>;
  }

  return (
    <div>
      <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-primary'}`}>Orders ({orders.length})</h2>
      
      {orders.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No orders yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {orders.map(order => (
            <div key={order.id} className={`border rounded-lg p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Order #{order.id}</p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {new Date(order.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  order.status === 'fulfilled' ? 'bg-green-100 text-green-800' :
                  order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {order.status}
                </span>
              </div>
              <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <strong>Items:</strong> {order.line_items?.length || 0}
              </p>
              <p className={`text-sm font-semibold ${isDarkMode ? 'text-primary' : 'text-primary'}`}>
                <strong>Total:</strong> ${(order.total_price / 100).toFixed(2)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Catalog Tab Component
const CatalogTab = () => {
  const { isDarkMode } = useTheme();
  const [blueprints, setBlueprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBlueprints();
  }, []);

  const fetchBlueprints = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/printify/catalog/blueprints');
      setBlueprints(response);
    } catch (err) {
      console.error('Error fetching blueprints:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredBlueprints = blueprints.filter(bp =>
    bp.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className={`text-center py-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading catalog...</div>;
  }

  return (
    <div>
      <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-primary'}`}>
        Product Catalog ({blueprints.length})
      </h2>
      
      <input
        type="text"
        placeholder="🔍 Search blueprints..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className={`w-full px-4 py-3 border rounded-lg mb-6 ${
          isDarkMode 
            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
        }`}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredBlueprints.map(blueprint => (
          <div key={blueprint.id} className={`border rounded-lg p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
            <h3 className={`font-bold text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{blueprint.title}</h3>
            <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{blueprint.description}</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {blueprint.id}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Webhooks Tab Component
const WebhooksTab = ({ shopId }) => {
  const { isDarkMode } = useTheme();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWebhooks();
  }, [shopId]);

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/printify/shops/${shopId}/webhooks`);
      setWebhooks(response);
    } catch (err) {
      console.error('Error fetching webhooks:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className={`text-center py-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading webhooks...</div>;
  }

  return (
    <div>
      <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-primary'}`}>
        Webhooks ({webhooks.length})
      </h2>
      
      {webhooks.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No webhooks configured.
        </div>
      ) : (
        <div className="space-y-4">
          {webhooks.map(webhook => (
            <div key={webhook.id} className={`border rounded-lg p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
              <p className={`font-bold text-lg mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{webhook.topic}</p>
              <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{webhook.url}</p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {webhook.id}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrintifyAdmin;
